import * as Sentry from "@sentry/react";
import React from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import AppRoutes from "./AppRoutes";
import AuthProvider from "./auth/contexts/AuthProvider";
import Loader from "./core/components/Loader";
import RouteDocumentHead from "./core/components/RouteDocumentHead";
import { DocumentHeadProvider } from "./core/contexts/DocumentHeadContext";
import ScrollToTop from "./core/components/ScrollToTop";
import { AuthProfileSync } from "./store/AuthProfileSync";
import QueryWrapper from "./core/components/QueryWrapper";
import SettingsProvider from "./core/contexts/SettingsProvider";
import SnackbarProvider from "./core/contexts/SnackbarProvider";
import usePageTracking from "./core/hooks/usePageTracking";
import "./core/css/global.css";
import { Box, IconButton, Toolbar } from "@material-ui/core";
import Chat from "./Chat";

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  integrations: [
    new Sentry.BrowserTracing({
      tracePropagationTargets: [
        "localhost",
        /^https:\/\/candidatos\.agnesrh\.com\.br/,
      ],
    }),
    new Sentry.Replay(),
  ],
});

export const messageSentry = (message: string): void => {
  if (process.env.NODE_ENV === "production") {
    Sentry.captureMessage(message);
  } else {
  }
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
      suspense: true,
    },
  },
});

function App() {
  usePageTracking();

  return (
    <React.Suspense
      fallback={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
            width: "100%",
          }}
        >
          <Loader />
        </Box>
      }
    >
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
        <QueryClientProvider client={queryClient}>
          <SettingsProvider>
            <QueryWrapper>
              <SnackbarProvider>
                <AuthProvider>
                  <DocumentHeadProvider>
                    <AuthProfileSync>
                      <RouteDocumentHead />
                      <ScrollToTop />
                      <Chat />
                      <AppRoutes />
                    </AuthProfileSync>
                  </DocumentHeadProvider>
                </AuthProvider>
              </SnackbarProvider>
            </QueryWrapper>
          </SettingsProvider>
          {/* <ReactQueryDevtools initialIsOpen /> */}
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </React.Suspense>
  );
}

export default App;
